import { requireDiscussionCacheTenantId } from '../tenant/discussionTenantScope';

const STORAGE_PREFIX = 'od_discussion_feed_v1';

function feedCacheKey(params) {
  const tenantId = requireDiscussionCacheTenantId(params?.tenantId);
  if (!tenantId) return null;
  const sort = String(params?.sort || 'latest');
  const contentTypes = Array.isArray(params?.postContentTypes)
    ? [...params.postContentTypes].map((t) => String(t || '').trim().toLowerCase()).filter(Boolean).sort().join(',')
    : '';
  const profileMode = String(params?.profileMode || 'personal');
  const parts = [
    sort,
    sort === 'recommended' ? String(params?.feedSeed || '') : '',
    `mode:${profileMode}`,
    String(params?.clubSlug || ''),
    String(params?.q || '').trim().toLowerCase(),
    String(params?.hashtagSlug || '').trim().toLowerCase(),
    contentTypes ? `types:${contentTypes}` : '',
    params?.onlyMyPosts
      ? `u:${params?.userId || ''}:${profileMode}:${String(params?.myPostsStatus || 'published')}`
      : 'all',
  ];
  return `${STORAGE_PREFIX}:${tenantId}:${parts.join('|')}`;
}

export function loadCachedDiscussionFeed(params) {
  if (typeof window === 'undefined') return null;
  const key = feedCacheKey(params);
  if (!key) return null;
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.threads)) return null;
    return {
      threads: parsed.threads,
      hasMore: !!parsed.hasMore,
      total: typeof parsed.total === 'number' ? parsed.total : null,
      savedAt: parsed.savedAt || 0,
    };
  } catch {
    return null;
  }
}

/** Default home-feed cache key — used for instant first paint. */
export function loadDefaultCachedDiscussionFeed(
  userId,
  tenantId,
  { sort = 'latest', feedSeed = '', profileMode = 'personal' } = {}
) {
  return loadCachedDiscussionFeed({
    tenantId,
    sort,
    feedSeed,
    clubSlug: '',
    q: '',
    onlyMyPosts: false,
    userId,
    profileMode,
  });
}

/** Drop all session feed list caches (e.g. after login so home feed re-shuffles). */
export function clearDiscussionFeedSessionCaches() {
  if (typeof window === 'undefined') return;
  try {
    for (let i = sessionStorage.length - 1; i >= 0; i -= 1) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith(`${STORAGE_PREFIX}:`)) sessionStorage.removeItem(key);
    }
  } catch {
    /* quota / private mode */
  }
}

export function saveCachedDiscussionFeed(params, payload) {
  if (typeof window === 'undefined') return;
  const key = feedCacheKey(params);
  if (!key) return;
  try {
    sessionStorage.setItem(
      key,
      JSON.stringify({
        threads: payload.threads || [],
        hasMore: !!payload.hasMore,
        total: payload.total ?? null,
        savedAt: Date.now(),
      })
    );
  } catch {
    /* quota / private mode */
  }
}

/** Keep session feed caches in sync after an in-feed vote. */
export function patchThreadVoteInCachedDiscussionFeeds(slug, patch, tenantId) {
  patchThreadInCachedDiscussionFeeds(slug, patch, tenantId);
}

/** Merge content fields into every cached feed row for a thread (edit, publish, etc.). */
export function patchThreadInCachedDiscussionFeeds(slug, patch, tenantId) {
  const s = String(slug || '').trim();
  const tid = requireDiscussionCacheTenantId(tenantId);
  if (!s || !tid || typeof window === 'undefined' || !patch || typeof patch !== 'object') return;
  const prefix = `${STORAGE_PREFIX}:${tid}:`;
  try {
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const key = sessionStorage.key(i);
      if (!key || !key.startsWith(prefix)) continue;
      const raw = sessionStorage.getItem(key);
      if (!raw) continue;
      try {
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed?.threads)) continue;
        let changed = false;
        const nextThreads = parsed.threads.map((t) => {
          if (String(t?.slug || '').trim() !== s) return t;
          changed = true;
          return { ...t, ...patch };
        });
        if (!changed) continue;
        sessionStorage.setItem(
          key,
          JSON.stringify({
            ...parsed,
            threads: nextThreads,
            savedAt: Date.now(),
          })
        );
      } catch {
        /* skip bad entry */
      }
    }
  } catch {
    /* quota / private mode */
  }
}

/** Drop a thread from every cached feed list for the active tenant (e.g. after delete). */
export function removeThreadFromCachedDiscussionFeeds(slug, tenantId) {
  const s = String(slug || '').trim();
  const tid = requireDiscussionCacheTenantId(tenantId);
  if (!s || !tid || typeof window === 'undefined') return;
  const prefix = `${STORAGE_PREFIX}:${tid}:`;
  try {
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const key = sessionStorage.key(i);
      if (!key || !key.startsWith(prefix)) continue;
      const raw = sessionStorage.getItem(key);
      if (!raw) continue;
      try {
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed?.threads)) continue;
        const next = parsed.threads.filter((t) => String(t?.slug || '').trim() !== s);
        if (next.length === parsed.threads.length) continue;
        sessionStorage.setItem(
          key,
          JSON.stringify({
            ...parsed,
            threads: next,
            total: typeof parsed.total === 'number' ? Math.max(0, parsed.total - 1) : parsed.total,
            savedAt: Date.now(),
          })
        );
      } catch {
        /* skip bad entry */
      }
    }
  } catch {
    /* quota / private mode */
  }
}
